// The organiser's read-only Status (Devin 2026-11-02, card f4270e09: "they
// can't publish, they can only see the status, read only"): today's
// competitions in syllabus order, the results pages' type chips, and each
// competition's way through its tabulation. Pure helpers; the screen is
// OrganizerStatus.jsx; rows come from GET /oireachtas/:id/organizer-status
// (the admin Status rows, projected: no judge or tabulator names).
//
// A copy of the admin's statusModel.js with organiser wording (nothing to
// press, no admin screens named), and verbatim copies of the admin
// tabulationFlow.js helpers it needs (src/Organizer/ManageOireachtas/ in the
// admin repo) — keep them in step.
import { compareComps } from "../../Teacher/Oireachtas/compSort";

// ---- copied from the admin's tabulationFlow.js --------------------------------------
export const isMultiRound = (row) => (row?.competition?.rounds || 1) > 1;

// The rounds before the recall: 1 and 2 of a multi-round competition, the
// only round otherwise.
export const preRecallRounds = (row) => (isMultiRound(row) ? [1, 2] : [1]);

const roundRow = (row, round) => (row?.roundRows || []).find((r) => r.round === round);

// Every judge sheet of the round signed off.
export const roundSigned = (row, round) => {
  const rr = roundRow(row, round);
  return !!rr && rr.slots.length > 0 && rr.slots.every((s) => s.signOff === "signed_off");
};

// A round can take scores once its dancers are known: rounds 1-2 always,
// a recall round only after the recall is published.
export const roundOpen = (row, round) => !(isMultiRound(row) && round >= 3) || !!row?.recallPublished;

// Where the competition stands, as the next thing the tabulator does.
//   { step: "no_draw" }
//   { step: "scores", round }      sheets still to enter or sign for `round`
//   { step: "recall" }             rounds 1-2 signed: print + publish the recall
//   { step: "results" }            every round signed: print, announce, publish
//   { step: "done" }               results published
// A publication corrected after it went up (an admin correction) stays
// pending until it is published again: { step: ..., republish: true } once its
// sheets are signed again.
export function compStep(row) {
  if (!row?.draw?.locked) return { step: "no_draw" };
  const corrected = row.corrected || {};
  const rounds = row.competition.rounds || 1;
  const before = preRecallRounds(row);
  const open = before.find((r) => !roundSigned(row, r));
  const later = () => {
    for (let r = before.length + 1; r <= rounds; r++) if (!roundSigned(row, r)) return r;
    return null;
  };
  if (row.resultsPublished) {
    if (!corrected.results) return { step: "done" };
    const unsigned = open || later();
    return unsigned ? { step: "scores", round: unsigned, republish: true } : { step: "results", republish: true };
  }
  if (open) return { step: "scores", round: open, ...(corrected.recall && row.recallPublished ? { republish: true } : {}) };
  if (isMultiRound(row) && (!row.recallPublished || corrected.recall)) {
    return { step: "recall", ...(row.recallPublished ? { republish: true } : {}) };
  }
  const r = later();
  if (r) return { step: "scores", round: r };
  return { step: "results" };
}

// Judge sheet states in a round, by slot letter: "missing" (nothing entered),
// "entered", "printed" or "signed_off".
export function sheetState(row, round, slot) {
  const s = (roundRow(row, round)?.slots || []).find((x) => x.slot === slot);
  if (!s) return "missing";
  if (s.signOff === "missing") return s.stored > 0 ? "entered" : "missing";
  return s.signOff;
}

// Today's competitions (Devin 2026-10-28): Enter Scores lists only the
// competitions with a round on today's stage schedule. dayKey is today as the
// schedule writes days ("YYYY-MM-DD", see eventDayKey). → a Set of
// competition ids, or null when the schedule has nothing on today (no
// schedule yet, or not an event day): then every competition is listed.
export function scheduledToday(schedule, dayKey) {
  const day = (schedule?.days || []).find((d) => d.day === dayKey);
  const ids = new Set();
  for (const stage of day?.stages || []) {
    for (const item of stage.items || []) {
      if (item.type === "round" && item.competition_id) ids.add(String(item.competition_id));
    }
  }
  return ids.size ? ids : null;
}

// Today in the event's time zone, as a schedule day key ("YYYY-MM-DD").
export function eventDayKey(timeZone, now = new Date()) {
  const opts = { year: "numeric", month: "2-digit", day: "2-digit" };
  let parts;
  try {
    parts = new Intl.DateTimeFormat("en-US", { ...opts, timeZone: timeZone || undefined }).formatToParts(now);
  } catch (e) {
    parts = new Intl.DateTimeFormat("en-US", opts).formatToParts(now); // unknown zone: this device's
  }
  const part = (type) => parts.find((p) => p.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

// ---- the Status model (the admin statusModel.js, organiser wording) ---------------------
// The public results pages' type chips, in Devin's order (2026-10-30; a copy
// of the web's src/Parent/Results/oireachtasResultsModel.js GROUPS).
export const TYPE_GROUPS = [
  ["Solo championships", ["solo_champ"]],
  ["Teams", ["team_2h", "team_3h", "team_4h", "team_6h", "team_8h"]],
  ["Premiere", ["premiere"]],
  ["Trad sets", ["trad_set"]],
  ["Treble reels", ["treble_reel"]],
  ["Figure and dance drama", ["figure", "dance_drama"]],
  ["Adults", ["adult_solo", "advanced_adult", "adult_team"]],
];
export const ALL = "All";
export const typeGroupOf = (type) => (TYPE_GROUPS.find(([, types]) => types.includes(type)) || ["Other"])[0];
// "All" first (mission control wants the whole day at a glance), then the
// groups present, in order
export const chipsOf = (rows) => {
  const present = new Set((rows || []).map((r) => typeGroupOf(r.competition.type)));
  return [ALL, ...[...TYPE_GROUPS.map(([g]) => g), "Other"].filter((g) => present.has(g))];
};
export const inChip = (row, chip) => !chip || chip === ALL || typeGroupOf(row.competition.type) === chip;

// Syllabus order (compareComps) on the competition's own group and gender,
// which the organiser rows carry.
export const sortRows = (rows) => [...(rows || [])].sort((a, b) => compareComps(a.competition, b.competition));

// Today's competitions (ids from tabulationFlow.scheduledToday; null = no
// schedule for today = everything).
export const todayRows = (rows, todayIds, showAll) =>
  !todayIds || showAll ? rows : rows.filter((r) => todayIds.has(String(r.competition._id)));

// ---- one round -------------------------------------------------------------------
const roundRowOf = (row, round) => (row.roundRows || []).find((r) => r.round === round);
// A round's sheets by View Scores' own state (tabulationFlow.sheetState, so
// the two screens agree — plan review 54cd343b R1-F3): entered = saved
// (entered, printed or signed off; a dancer left blank who did not dance
// still counts), printed = sign-off sheet out, signed = signed off.
export function roundCounts(row, round) {
  const slots = roundRowOf(row, round)?.slots || [];
  const st = slots.map((s) => sheetState(row, round, s.slot));
  return {
    seats: slots.length,
    entered: st.filter((x) => x !== "missing").length,
    printed: st.filter((x) => x === "printed").length,
    signed: st.filter((x) => x === "signed_off").length,
    started: st.some((x) => x !== "missing"),
  };
}
const roundDetail = (c) => {
  if (!c.started && !c.signed) return "—";
  const parts = [`${c.entered}/${c.seats} entered`];
  if (c.printed) parts.push(`${c.printed} printed`);
  if (c.signed) parts.push(`${c.signed} signed`);
  return parts.join(" · ");
};

// Red = waiting on the organiser (Devin 2026-11-03): sign-off sheets printed
// but not signed yet, or a recall / results checked and ready to publish.
// The rounds open now with printed, unsigned sheets → [{ round, printed }].
export function awaitingSignOff(row) {
  if (!row?.draw?.locked) return [];
  return (row.roundRows || [])
    .filter((rr) => roundOpen(row, rr.round) && !roundSigned(row, rr.round))
    .map((rr) => ({ round: rr.round, printed: roundCounts(row, rr.round).printed }))
    .filter((x) => x.printed > 0);
}
const roundsWord = (rounds) =>
  rounds.length === 1 ? `Round ${rounds[0]}` : `Rounds ${rounds.slice(0, -1).join(", ")} and ${rounds[rounds.length - 1]}`;

// ---- the progress track ------------------------------------------------------------
// [{ key, label, state: done|current|waiting, alert, detail, round? }]
// alert: the step waits on the organiser (printed sheets to sign, or checked
// and ready to publish)
// Multi-round: Round 1, Round 2, Recall, Round 3 (… any later rounds), Results.
// One round: Round 1, Results.
export function stepsOf(row) {
  const rounds = row.competition.rounds || 1;
  const step = compStep(row);
  const drawn = step.step !== "no_draw";
  const before = preRecallRounds(row);
  const steps = [];
  const roundStep = (r) => {
    const c = roundCounts(row, r);
    const recallRound = isMultiRound(row) && r > before.length;
    // each round from its own sheets (rounds 1 and 2 are typed side by
    // side — plan review 54cd343b R1-F2): done when all signed off, current
    // when any sheet is in or it is the next round to work on, else waiting;
    // a recall round waits for its recall
    let state = "waiting";
    if (!drawn || !roundOpen(row, r)) state = "waiting";
    else if (roundSigned(row, r)) state = "done";
    else if (c.started || (step.step === "scores" && step.round === r)) state = "current";
    let detail = roundDetail(c);
    if (recallRound && !row.recallPublished) detail = "after the recall";
    const ci = roundRowOf(row, r)?.checkIn;
    if (recallRound && row.recallPublished && ci?.taken) {
      detail += ci.expected ? ` · ${ci.checkedIn} of ${ci.expected} checked in` : ` · ${ci.checkedIn} checked in`;
    }
    return { key: `r${r}`, label: `Round ${r}`, state, alert: state === "current" && c.printed > 0, detail, round: r };
  };
  const pubStep = (kind, label) => {
    const published = kind === "recall" ? row.recallPublished : row.resultsPublished;
    const corrected = !!row.corrected?.[kind];
    const checked = !!row.checks?.[kind]?.checked;
    let state = "waiting";
    let detail = "";
    if (published && !corrected) {
      state = "done";
      detail = "Published";
    } else if (drawn && step.step === kind) {
      state = "current";
      if (published && corrected) detail = checked ? "Corrected — to be published again" : "Corrected — being checked";
      else detail = checked ? "Checked" : "Being checked";
    } else if (published && corrected) {
      detail = "Corrected — to be published again";
    }
    return { key: kind, label, state, alert: state === "current" && checked, detail };
  };
  before.forEach((r) => steps.push(roundStep(r)));
  if (isMultiRound(row)) {
    steps.push(pubStep("recall", "Recall"));
    for (let r = before.length + 1; r <= rounds; r++) steps.push(roundStep(r));
  }
  steps.push(pubStep("results", "Results"));
  return steps;
}

// ---- where it is now ----------------------------------------------------------------
// { short, line, tone: idle|busy|attn|done|warn }: `short` for the pill,
// `line` under the track
export function headlineOf(row) {
  const s = compStep(row);
  if (s.step === "no_draw") return { short: "Not drawn", line: "Draw not locked", tone: "idle" };
  const waiting = s.step === "done" ? [] : awaitingSignOff(row);
  if (waiting.length) {
    const n = waiting.reduce((t, x) => t + x.printed, 0);
    const line = `${roundsWord(waiting.map((x) => x.round))} — ${n} sign-off sheet${n === 1 ? "" : "s"} printed, not signed yet`; // no judge count: a judge may sit both rounds (code review 160cb260 R1-F1)
    return { short: "Awaiting sign-off", line: s.republish ? `${line} — corrected after publishing` : line, tone: "attn" };
  }
  if (s.step === "scores") {
    const c = roundCounts(row, s.round);
    const recallOut = isMultiRound(row) && s.round > preRecallRounds(row).length && row.recallPublished;
    const line = `${recallOut ? "Recall published — " : ""}Round ${s.round} — ${c.entered} of ${c.seats} sheets entered, ${c.signed} signed off`;
    return {
      short: `Round ${s.round}`,
      line: s.republish ? `${line} — corrected after publishing` : line,
      tone: s.republish ? "warn" : c.started || c.signed ? "busy" : "idle",
    };
  }
  if (s.step === "recall" || s.step === "results") {
    const checked = !!row.checks?.[s.step]?.checked;
    const what = s.step === "recall" ? "Recall" : "Results";
    if (checked) {
      return {
        short: `${what} ready`,
        line: s.republish ? `${what} corrected and checked — to be published again` : `${what} checked — ready to publish`,
        tone: "attn",
      };
    }
    return {
      short: `Checking ${what.toLowerCase()}`,
      line:
        s.step === "recall"
          ? "Rounds 1 and 2 signed off — the recall is being checked"
          : "Every sheet signed off — the results are being checked",
      tone: s.republish ? "warn" : "busy",
    };
  }
  return { short: "Published", line: "Results published", tone: "done" };
}

// ---- the shown list in numbers -----------------------------------------------------------
export function summaryOf(rows) {
  const out = { total: rows.length, notDrawn: 0, inProgress: 0, signOff: 0, ready: 0, published: 0 };
  for (const r of rows) {
    const s = compStep(r);
    if (s.step === "no_draw") out.notDrawn += 1;
    else if (s.step === "done") out.published += 1;
    // the headline's order: sheets to sign first (code review 160cb260 R1-F2)
    else if (awaitingSignOff(r).length) out.signOff += 1;
    else if ((s.step === "recall" || s.step === "results") && r.checks?.[s.step]?.checked) out.ready += 1;
    else out.inProgress += 1;
  }
  return out;
}
